import {
   ChangeDetectorRef,
   ContentChild,
   Directive,
   EventEmitter,
   HostListener,
   Input,
   OnDestroy,
   Output,
   TemplateRef,
   ViewChild,
} from '@angular/core';
import { ChartDataPoint, ChartSeriesDataPoint } from '@ndt/reports/shared/charts/chart-data-point-interface';
import { Color, LegendPosition, ScaleType } from '@swimlane/ngx-charts';
import { curveBumpX } from 'd3-shape';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

export const ColorPickerPresets: string[] = [
   '#7D00AA',
   '#FF50CA',
   '#82D36E',
   '#FAA332',
   '#01A7E1',
   '#000000',
   '#FFFFFF',
];

export const defaultColorScheme = {
   domain: ColorPickerPresets.filter((c) => c !== '#FFFFFF'),
   group: ScaleType.Linear,
   name: '',
   selectable: true,
};

@Directive()
export class ChartBaseProperties<T extends ChartSeriesDataPoint | ChartDataPoint> {
   @ContentChild('tooltipTemplateRef') customTooltipTemplateRef: TemplateRef<any>;

   @ContentChild('seriesTooltipTemplateRef') customSeriesTooltipTemplateRef: TemplateRef<any>;

   @ViewChild('chart') chart: any;

   @Input() data: T[];

   @Input() dataLabelFormatting: any;

   @Input() seriesData: T[];

   @Input() referenceLines: any = [];

   @Input() view: [number, number] | undefined = undefined;

   @Input() legend: boolean = false;

   @Input() legendPosition: LegendPosition = LegendPosition.Right;

   @Input() lineYAxisLabel: string;

   @Input() showLabels: boolean = true;

   @Input() animations: boolean = true;

   @Input()
   set barPadding(value: number | undefined) {
      // Cast potentially undefined bar padding to numeric value
      // so xScale range calculations dont produce NaN values
      this._barPadding = value ?? 0;
   }
   get barPadding(): number {
      return this._barPadding;
   }
   private _barPadding: number = 10;

   @Input() xAxis: boolean = true;

   @Input() yAxis: boolean = true;

   @Input() showVerticalBarLabels: boolean = false;

   @Input() verticalBarLabelFormatting: (val: any) => string;

   @Input() showYAxisLabel: boolean = true;

   @Input() showXAxisLabel: boolean = true;

   @Input() xAxisLabel: string = '';

   @Input() yAxisLabel: string = '';

   @Input() trimXAxisTicks: boolean = true;

   @Input() trimYAxisTicks: boolean = true;

   @Input() maxXAxisTickLength: number = 16;

   @Input() maxYAxisTickLength: number = 16;

   @Input() xAxisTickFormatting: (() => string) | ((val: Date) => string);

   @Input() yAxisTickFormatting: () => string;

   @Input() timeline: boolean = true;

   @Input() colorScheme: Color = defaultColorScheme;

   /* Specialty Inputs */

   @Input() curve = curveBumpX;

   /**
    * Whether to show a 1px-high bar in bar charts when the data point is falsy.  Defaults to false.
    */
   @Input() noBarWhenZero: boolean = false;

   /**
    * Whether x-axis labels should be rotated to fit. Defaults to true.
    */
   @Input() rotateXAxisTicks: boolean = true;

   /**
    * The color scheme type. Defaults to ordinal.
    */
   @Input() schemeType: ScaleType = ScaleType.Ordinal;

   /**
    * Whether to show data labels.  Defaults to false.
    */
   @Input() showDataLabel: boolean = false;

   /**
    * Show or hide the labels of a pie chart. Defaults to false.
    */
   @Input() labels: boolean = false;

   /**
    * Make the radius of each slice proportional to it's value on a pie chart. Defaults to false.
    */
   @Input() explodeSlices: boolean = false;

   /**
    * Should doughnut instead of pie slices. Defaults to false.
    */
   @Input() doughnut: boolean = false;

   /**
    * Callback function that defines if pie grid should get cursor styling.
    */
   @Input() canClickPieInGridCb: (data: any) => boolean = () => false;

   @Input() xScaleMin: number | undefined;

   @Input() xScaleMax: number | undefined;
}

@Directive()
export class ChartBase<T extends ChartSeriesDataPoint | ChartDataPoint>
   extends ChartBaseProperties<T>
   implements OnDestroy
{
   @HostListener('window:resize', ['$event'])
   onResize(event) {
      this.windowResize$.next();
   }

   @Output() select = new EventEmitter<any>();

   @Output() activate = new EventEmitter<any>();

   @Output() deactivate = new EventEmitter<any>();

   windowResize$ = new Subject<void>();

   destroyed$ = new Subject<void>();

   constructor(protected changeDetectorRef: ChangeDetectorRef) {
      super();
      // Wait for parent containers to finish recalculating their heights and widths and then update chart
      this.windowResize$.pipe(takeUntil(this.destroyed$), debounceTime(500)).subscribe(() => {
         if (!this.view) {
            this.triggerUpdate();
         }
      });
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
   }

   onSelect(data): void {
      this.select.emit(data);
   }

   onActivate(data): void {
      this.activate.emit(data);
   }

   onDeactivate(data): void {
      this.deactivate.emit(data);
   }

   triggerUpdate() {
      this.data = this.data ? [...this.data] : [];
      this.chart?.cd?.markForCheck();
      this.chart?.chart?.cd?.markForCheck();
      this.changeDetectorRef.detectChanges();
   }
}
